// ─── What: the dashed asset price lines and their right-hand axes ─────────────
//
// Each asset gets its own price scale — they share the x axis and nothing else.
// The filtered/extended arrays are returned because the crosshair needs the same
// data to place its dots and tooltip rows.

import * as d3 from 'd3';
import type { OHLCVPoint } from '../../../../api/types';
import type { AssetOverlayData } from '../../../../hooks/useAssetPriceOverlay';
import { getAssetColor } from '../../../../hooks/useAssetPriceOverlay';
import { fmtPrice } from '../../chartFormat';
import { MARGIN } from '../layout';
import type { ChartGroup, DrawCtx } from '../drawContext';

/** The per-asset price scale. Derived in two places, so defined once. */
export function assetPriceScale(
    data: OHLCVPoint[],
    probHeight: number
): d3.ScaleLinear<number, number> {
    const extent = d3.extent(data, d => d.close) as [number, number];
    const pad = (extent[1] - extent[0]) * 0.08;
    return d3
        .scaleLinear()
        .domain([extent[0] - pad, extent[1] + pad])
        .range([probHeight - 4, MARGIN.top]);
}

/** One entry per loaded asset, in the same order. Empty when it had too few points. */
export function drawAssetOverlays(
    ctx: DrawCtx,
    probGroup: ChartGroup,
    loadedAssets: AssetOverlayData[]
): OHLCVPoint[][] {
    const { svg, layout, colors, xScale, xDomain, now } = ctx;
    const w = layout.w;

    return loadedAssets.map((asset, assetIdx) => {
        const filtered = asset.data.filter(
            d => d.t >= xDomain[0] && d.t <= xDomain[1]
        );
        if (filtered.length < 2) return [];
        const last = filtered[filtered.length - 1];
        if (last.t < now && now <= xDomain[1])
            filtered.push({ ...last, t: now, vol: 0 });

        const priceScale = assetPriceScale(filtered, layout.probHeight);
        const axisX = MARGIN.left + w + (assetIdx === 0 ? 6 : 50);
        const color = getAssetColor(assetIdx, colors.isDark);

        svg.append('g')
            .selectAll('text')
            .data(priceScale.ticks(4))
            .join('text')
            .attr('x', axisX + 2)
            .attr('y', (d: number) => priceScale(d) + 3)
            .attr('fill', color)
            .attr('font-size', '8px')
            .attr('font-family', 'monospace')
            .attr('font-weight', 600)
            .text((d: number) => fmtPrice(d));

        svg.append('text')
            .attr('x', axisX + 2)
            .attr('y', MARGIN.top - 6)
            .attr('fill', color)
            .attr('font-size', '9px')
            .attr('font-family', 'monospace')
            .attr('font-weight', 700)
            .text(asset.ticker);

        probGroup
            .append('path')
            .datum(filtered)
            .attr(
                'd',
                d3
                    .line<OHLCVPoint>()
                    .x(d => xScale(d.t))
                    .y(d => priceScale(d.close))
                    .curve(d3.curveMonotoneX)
            )
            .attr('fill', 'none')
            .attr('stroke', color)
            .attr('stroke-width', 2.5)
            .attr('stroke-dasharray', '4 4')
            .attr('opacity', 0.85);

        return filtered;
    });
}
